import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';

@Component({
    selector: 'app-sb-forgot-password',
    template: `
        <div class="card">
            <header>
                <h2>Reset password</h2>
            </header>
            <main class="pt-0">
                <ng-container *ngIf="!expiredToken; else tokenExpired">
                    <p class="mb-4">
                        Enter the email address associated with your SWIP account. We will
                        send you an email with instructions on how to reset your password.
                    </p>
                </ng-container>
                <form
                    [formGroup]="forgotPasswordForm"
                    (ngSubmit)="submit()"
                    id="forgotPasswordForm"
                >
                    <mat-form-field appearance="fill">
                        <mat-label>Email</mat-label>
                        <input matInput type="email" formControlName="email" />
                        <mat-error *ngIf="forgotPasswordForm.get('email').errors">
                            {{
                                forgotPasswordForm.get('email').errors?.required
                                    ? 'Email is required'
                                    : 'Invalid email provided'
                            }}
                        </mat-error>
                    </mat-form-field>
                    <ng-container
                        *ngIf="isSubmitted"
                        [ngTemplateOutlet]="
                            forgotPasswordForm.errors ? formError : formSuccess
                        "
                    ></ng-container>
                </form>
            </main>
            <footer>
                <a class="btn secondary" routerLink="/login">Back to login</a>
                <button
                    class="btn primary"
                    form="forgotPasswordForm"
                    type="submit"
                    [disabled]="passwordReset"
                >
                    Submit
                </button>
            </footer>
        </div>
        <ng-template #formError>
            <div role="alert" class="critical mt-3">
                <h4 class="mb-0">Password was not reset</h4>
                <ng-container *ngIf="forgotPasswordForm.errors?.server">
                    {{ this.forgotPasswordForm.errors.server }}
                </ng-container>
            </div>
        </ng-template>
        <ng-template #formSuccess>
            <div role="alert" class="success mt-3" *ngIf="passwordReset">
                <h4 class="mb-0">An email has been sent with a reset password link.</h4>
                Use it to change your password.
            </div>
        </ng-template>
        <ng-template #tokenExpired>
            <p class="mb-4">
                The reset link you used has expired. Please submit your email again to
                request a new one.
            </p>
        </ng-template>
    `,
    styles: [
        `
            .card {
                max-width: 375px;
                min-width: auto;
                /* TODO: use utility classes */
                margin-top: 5rem;
                margin-left: auto;
                margin-right: auto;
            }
        `
    ]
})
export class SbForgotPasswordComponent {
    forgotPasswordForm: UntypedFormGroup;
    isSubmitted = false;
    passwordReset = false;
    expiredToken = false;

    constructor(
        private config: AppConfigQuery,
        private http: HttpClient,
        private route: ActivatedRoute,
        private _fb: UntypedFormBuilder
    ) {
        this.forgotPasswordForm = this._fb.group({
            email: ['', [Validators.email, Validators.required]]
        });

        // The user will be routed here if the previous reset password token expired when the user tried to use it.
        this.expiredToken = this.route.snapshot.queryParams.expired;
    }

    submit() {
        this.isSubmitted = true;

        if (this.forgotPasswordForm.invalid) {
            return;
        }

        const email = this.forgotPasswordForm.value.email.toLowerCase();
        this.http
            .get(this.config.get().gatewayBaseUrl + '/password/reset', {
                params: { email: email }
            })
            .subscribe(
                (response) => {
                    if (response.hasOwnProperty('error')) {
                        this.forgotPasswordForm.setErrors({
                            server: response['error']
                        });
                    } else {
                        this.passwordReset = true;
                    }
                },
                (error) => {
                    this.forgotPasswordForm.setErrors({
                        server: error.message
                    });
                }
            );
    }
}
